import React, { useState } from 'react';
import laboratoryInfo from '../config';
import { useData } from '../contexts/DataContext';
import { FaFileInvoice } from 'react-icons/fa';
import DocumentPreview from './DocumentPreview';

const QuoteGenerator = ({ order, quoteNumber, showButton = true, buttonLabel = "Devis" }) => {
  const { products, clients } = useData();
  const [showPreview, setShowPreview] = useState(false);

  const currentOrder = order || {};
  const practitionerClient = clients.find(c => c.id === (currentOrder?.clientId || currentOrder?.client_id));
  const items = [];
  let total = 0;

  const currentId = currentOrder.id || 'preview';
  // Use sequential Case Number if available
  const generatedQuoteNumber = quoteNumber || currentOrder.caseNumber || (typeof currentId === 'string' ? currentId.substring(0, 8).toUpperCase() : 'PREVIEW');
  const targetId = `quote-img-${currentId}`;
  const fileName = `devis-${currentOrder.patientName || 'sans-nom'}`;

  // Calculate items
  if (currentOrder.teethConfig) {
    if (Array.isArray(currentOrder.teethConfig)) {
      // New Array Format
      currentOrder.teethConfig.forEach(line => {
        const { productId, teeth, details } = line;
        const product = products.find(p => p.id === productId);

        if (product) {
          let priceKey = 'priceClassique';
          if (currentOrder.gamme === 'paris') priceKey = 'priceParis';
          if (currentOrder.gamme === 'eco') priceKey = 'priceEco';

          const unitPrice = product[priceKey];
          const quantity = line.quantity !== undefined && line.quantity !== null && !isNaN(Number(line.quantity)) 
              ? Number(line.quantity) 
              : (teeth.length || 1);
          const itemPrice = unitPrice * quantity;
          const toothLabel = teeth.map(t => t === 'HAUT' ? 'Haut' : t === 'BAS' ? 'Bas' : t).join(', ');
          let description = `${product.name}`;

          if (details && details.teinte) {
            description += `, Teinte ${details.teinte}`;
          }

          items.push({ toothId: toothLabel, description, quantity, unitPrice, price: itemPrice });
          total += itemPrice;
        }
      });
    } else {
      // Legacy Object Format
      Object.entries(currentOrder.teethConfig).forEach(([toothId, config]) => {
        const { productId, details } = config;
        const product = products.find(p => p.id === productId);

        if (product) {
          let priceKey = 'priceClassique';
          if (currentOrder.gamme === 'paris') priceKey = 'priceParis';
          if (currentOrder.gamme === 'eco') priceKey = 'priceEco';

          let unitPrice = product[priceKey];
          let itemPrice = unitPrice;
          let description = `${product.name}`;

          if (details && details.teinte) {
            description += `, Teinte ${details.teinte}`;
          }

          items.push({ toothId, description, quantity: 1, unitPrice, price: itemPrice });
          total += itemPrice;
        }
      });
    }
  }

  return (
    <>
      {showButton && (
        <button
          className="btn btn-sm btn-outline-primary"
          onClick={(e) => { e.stopPropagation(); setShowPreview(true); }}
          title="Générer Devis Image"
        >
          <FaFileInvoice className={buttonLabel ? "me-1" : ""} /> {buttonLabel}
        </button>
      )}

      {showPreview && (
        <DocumentPreview
          targetId={targetId}
          fileName={fileName}
          onClose={() => setShowPreview(false)}
        />
      )}

      {/* Hidden Template for Image Capture */}
      <div id={targetId} style={{ display: 'none' }}>
        <div style={{ backgroundColor: 'white', padding: '20mm', color: 'black', width: '210mm', boxSizing: 'border-box', fontFamily: 'Arial, sans-serif' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '40px', borderBottom: '2px solid #333', paddingBottom: '20px' }}>
            <div>
              <h2 style={{ margin: '0 0 10px 0', color: '#1e40af' }}>{laboratoryInfo.name}</h2>
              <p style={{ margin: '2px 0', fontSize: '13px' }}>{laboratoryInfo.address}</p>
              <p style={{ margin: '2px 0', fontSize: '13px' }}>Tel: {laboratoryInfo.phone}</p>
              <p style={{ margin: '2px 0', fontSize: '13px' }}>{laboratoryInfo.email}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <h1 style={{ margin: '0 0 10px 0', color: '#1e40af', fontSize: '28px' }}>DEVIS</h1>
              <p style={{ margin: '2px 0', fontWeight: 'bold', fontSize: '16px' }}>N° {generatedQuoteNumber}</p>
              <p style={{ margin: '2px 0', fontSize: '14px' }}>Date: {new Date().toLocaleDateString()}</p>
            </div>
          </div>

          <div style={{ marginBottom: '40px', display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{ width: '50%', border: '1px solid #e2e8f0', padding: '20px', borderRadius: '8px', backgroundColor: '#f8fafc' }}>
              <h3 style={{ marginTop: '0', fontSize: '14px', color: '#64748b', textTransform: 'uppercase', marginBottom: '10px' }}>À l'attention de:</h3>
              <p style={{ fontWeight: 'bold', margin: '5px 0', fontSize: '16px', color: '#1e293b' }}>Docteur {currentOrder.practitionerName}</p>
              {practitionerClient?.address && <p style={{ margin: '5px 0', fontSize: '13px', color: '#475569' }}>{practitionerClient.address}</p>}
              <p style={{ margin: '5px 0', fontSize: '14px' }}>Patient: {currentOrder.patientName}</p>
              {(currentOrder.patientReference || currentOrder.patient_reference) && (
                <p style={{ margin: '5px 0', fontSize: '14px' }}>Référence : <strong>{currentOrder.patientReference || currentOrder.patient_reference}</strong></p>
              )}
            </div>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '40px' }}>
            <thead>
              <tr style={{ backgroundColor: '#1e40af', color: 'white' }}>
                <th style={{ textAlign: 'left', padding: '14px' }}>Désignation des travaux</th>
                <th style={{ textAlign: 'center', padding: '14px 4px', width: '140px' }}>Dent</th>
                <th style={{ textAlign: 'center', padding: '14px', width: '70px' }}>Quantité</th>
                <th style={{ textAlign: 'right', padding: '14px', width: '90px' }}>P.U. HT</th>
                <th style={{ textAlign: 'right', padding: '14px', width: '100px' }}>Montant HT</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => {
                const qty = item.quantity || 1;
                const unitPrice = item.unitPrice || (item.price / qty);
                return (
                  <tr key={i} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '14px', fontSize: '14px' }}>{item.description}</td>
                    <td style={{ textAlign: 'center', padding: '14px 4px', fontSize: '13px', fontWeight: 'bold', lineHeight: '1.4' }}>{item.toothId}</td>
                    <td style={{ textAlign: 'center', padding: '14px', fontSize: '14px' }}>{qty}</td>
                    <td style={{ textAlign: 'right', padding: '14px', fontSize: '14px' }}>{Number(unitPrice).toFixed(2)} €</td>
                    <td style={{ textAlign: 'right', padding: '14px', fontSize: '14px', fontWeight: 'bold' }}>{item.price.toFixed(2)} €</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
            <div style={{ width: '300px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderTop: '2px solid #1e40af', fontSize: '20px', fontWeight: 'bold', color: '#1e40af' }}>
                <span>TOTAL NET:</span>
                <span>{total.toFixed(2)} €</span>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '30px', marginTop: '40px', paddingTop: '20px', borderTop: '2px solid #e2e8f0', fontSize: '12px' }}>
            <div>
              <h4 style={{ margin: '0 0 8px 0', color: '#1e40af', fontSize: '13px' }}>MENTIONS DMSM & TRAÇABILITÉ</h4>
              <p style={{ margin: '3px 0', fontSize: '11px', color: '#475569', lineHeight: '1.4' }}>
                <strong>Dispositif Médical Sur Mesure (DMSM)</strong> en cours de fabrication conforme aux spécifications prescrites.<br />
                Ce devis inclut la déclaration de conformité qui accompagnera la facture finale et le dispositif à la livraison.
              </p>
              <div style={{ marginTop: '10px', backgroundColor: '#f8fafc', padding: '10px', borderRadius: '6px', border: '1px dashed #cbd5e1' }}>
                <span style={{ fontSize: '11px', color: '#64748b' }}><strong>Validité du Devis :</strong></span>
                <div style={{ fontSize: '11px', color: '#334155', marginTop: '4px' }}>
                  Ce devis est valable 3 mois à compter de sa date d'émission.
                </div>
              </div>
            </div>
            <div style={{ border: '1px solid #cbd5e1', borderRadius: '8px', padding: '10px', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: '100px' }}>
              <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#475569' }}>Visa Laboratoire</span>
              <div style={{ textAlign: 'center', margin: '5px 0', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <img src="/signature.png" alt="Signature" style={{ height: '50px', maxHeight: '50px', objectFit: 'contain' }} />
              </div>
              <div style={{ borderTop: '1px dashed #cbd5e1', paddingTop: '5px', fontSize: '10px', color: '#94a3b8' }}>
                Cachet & Signature
              </div>
            </div>
          </div>

          <div style={{
            marginTop: '30px',
            textAlign: 'center',
            fontSize: '10px',
            color: '#94a3b8',
            borderTop: '1px solid #f1f5f9',
            paddingTop: '10px'
          }}>
            <p style={{ fontWeight: 'bold', margin: '0 0 4px 0' }}>{laboratoryInfo.fullName} - SIRET: {laboratoryInfo.siret} - TVA: {laboratoryInfo.tva}</p>
            <p style={{ margin: '2px 0', fontStyle: 'italic' }}>Système de management de la qualité conforme aux exigences de la norme ISO 13485 (Dispositifs Médicaux)</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default QuoteGenerator;
